import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Stack,
  Paper,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import { apiFetch } from '../../../api/client';
import { TIPOS_CAMPO, OPCOES_CONFORMIDADE } from '../utils/arcaFormConstants';

function uid() {
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function novoCampo(ordem, tipo = 'SELECAO_UNICA') {
  return {
    _uid: uid(),
    chave: tipo === 'SECAO' ? `sec_${ordem + 1}` : `campo_${ordem + 1}`,
    label: tipo === 'SECAO' ? 'Novo tópico' : 'Novo critério',
    tipo,
    obrigatorio: tipo === 'SELECAO_UNICA',
    ordem,
    secao: '',
    pontuacaoMax: tipo === 'SELECAO_UNICA' ? 1 : null,
    opcoes: tipo === 'SELECAO_UNICA' ? [...OPCOES_CONFORMIDADE] : []
  };
}

function mapCamposFromApi(list) {
  if (!Array.isArray(list) || !list.length) {
    return [novoCampo(0, 'SECAO'), novoCampo(1, 'SELECAO_UNICA')];
  }
  return list.map((c, i) => ({
    _uid: uid(),
    chave: c.chave,
    label: c.label,
    tipo: c.tipo,
    obrigatorio: !!c.obrigatorio,
    ordem: c.ordem ?? i,
    secao: c.secao || '',
    pontuacaoMax:
      c.pontuacao_max != null ? Number(c.pontuacao_max) : c.tipo === 'SELECAO_UNICA' ? 1 : null,
    opcoes: Array.isArray(c.opcoes)
      ? c.opcoes
      : c.opcoes
        ? [].concat(c.opcoes)
        : c.tipo === 'SELECAO_UNICA'
          ? [...OPCOES_CONFORMIDADE]
          : []
  }));
}

/**
 * Builder mutável: adicionar/remover tópicos e critérios com pontuação (C=1).
 * Templates PUBLICADOS abrem em leitura; botão cria nova versão editável.
 */
export default function ArcaFormularioBuilder({ open, templateId, onClose, onSaved, onTemplateChanged }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forking, setForking] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({ id: null, nome: '', status: '', modoPontuacao: 'C_NC_NA' });
  const [campos, setCampos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const loadSeq = useRef(0);

  useEffect(() => {
    if (!open) {
      loadSeq.current += 1;
      setLoading(false);
      setSaving(false);
      setForking(false);
      setError('');
      setCampos([]);
      setMeta({ id: null, nome: '', status: '', modoPontuacao: 'C_NC_NA' });
      setActiveId(null);
      return;
    }
    if (!templateId) {
      setError('Template inválido.');
      setLoading(false);
      return;
    }
    setActiveId(templateId);
  }, [open, templateId]);

  useEffect(() => {
    if (!open || !templateId) return;
    const idToLoad = templateId;
    const seq = ++loadSeq.current;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await apiFetch(`/api/arca/formularios/templates/${idToLoad}`);
        const data = await res.json().catch(() => ({}));
        if (seq !== loadSeq.current) return;
        if (!res.ok) throw new Error(data.error || `Erro ao carregar template (${res.status})`);
        setMeta({
          id: data.id,
          nome: data.nome || 'Template',
          status: data.status,
          modoPontuacao: data.modo_pontuacao || 'C_NC_NA'
        });
        setCampos(mapCamposFromApi(data.campos));
      } catch (e) {
        if (seq !== loadSeq.current) return;
        setError(e.message || 'Falha ao carregar');
        setCampos([]);
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    })();
  }, [open, templateId]);

  const readOnly = meta.status === 'PUBLICADO' || meta.status === 'ARQUIVADO';

  const updateCampo = (idx, patch) => {
    setCampos((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const move = (idx, dir) => {
    setCampos((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next.map((c, i) => ({ ...c, ordem: i }));
    });
  };

  const criarVersaoEditavel = async () => {
    const id = activeId || templateId;
    if (!id) return;
    setForking(true);
    setError('');
    try {
      const res = await apiFetch(`/api/arca/formularios/templates/${id}/nova-versao`, {
        method: 'POST'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao criar nova versão');
      setActiveId(data.id);
      onTemplateChanged?.(data.id);
      onSaved?.();
    } catch (e) {
      setError(e.message || 'Erro ao criar versão');
    } finally {
      setForking(false);
    }
  };

  const handleSave = async () => {
    const id = activeId || templateId;
    if (!id || readOnly) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        campos: campos.map((c, i) => ({
          chave: c.chave,
          label: c.label,
          tipo: c.tipo,
          obrigatorio: !!c.obrigatorio,
          ordem: i,
          secao: c.secao || null,
          pontuacaoMax: c.tipo === 'SELECAO_UNICA' ? c.pontuacaoMax ?? 1 : null,
          opcoes:
            c.tipo === 'SELECAO_UNICA' || c.tipo === 'SELECAO_MULTIPLA'
              ? c.opcoes?.length
                ? c.opcoes
                : OPCOES_CONFORMIDADE
              : null
        }))
      };
      const res = await apiFetch(`/api/arca/formularios/templates/${id}/campos`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar campos');
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        Builder — {meta.nome || (loading ? '…' : 'Template')}
        {meta.status === 'PUBLICADO' && (
          <Chip size="small" label="Publicado" color="success" sx={{ ml: 1 }} />
        )}
        {meta.status === 'RASCUNHO' && (
          <Chip size="small" label="Rascunho (editável)" color="warning" sx={{ ml: 1 }} />
        )}
        <Typography variant="caption" display="block" color="text.secondary">
          Pontuação: C = 1 · NC = 0 · NA = ignora · média = % conformidade. Adicione ou remova tópicos
          livremente.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {readOnly && !loading && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<EditIcon />}
                disabled={forking}
                onClick={criarVersaoEditavel}
              >
                {forking ? 'Criando…' : 'Criar versão editável'}
              </Button>
            }
          >
            Template publicado fica em somente leitura. Crie uma nova versão em rascunho para adicionar ou
            remover tópicos.
          </Alert>
        )}

        {loading ? (
          <Box display="flex" alignItems="center" gap={1.5} py={4} justifyContent="center">
            <CircularProgress size={28} />
            <Typography color="text.secondary">Carregando campos…</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                disabled={readOnly}
                startIcon={<AddIcon />}
                onClick={() => setCampos((prev) => [...prev, novoCampo(prev.length, 'SECAO')])}
              >
                Tópico
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                disabled={readOnly}
                startIcon={<AddIcon />}
                onClick={() => setCampos((prev) => [...prev, novoCampo(prev.length, 'SELECAO_UNICA')])}
              >
                Critério (+1 pt)
              </Button>
              {TIPOS_CAMPO.filter((t) => !['SECAO', 'SELECAO_UNICA'].includes(t.tipo)).map((t) => (
                <Button
                  key={t.tipo}
                  size="small"
                  variant="outlined"
                  disabled={readOnly}
                  startIcon={<AddIcon />}
                  onClick={() => setCampos((prev) => [...prev, novoCampo(prev.length, t.tipo)])}
                >
                  {t.label}
                </Button>
              ))}
            </Stack>

            {campos.length === 0 && (
              <Alert severity="warning">Nenhum campo. Adicione um tópico ou critério.</Alert>
            )}

            {campos.map((campo, idx) => (
              <Paper
                key={campo._uid}
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: campo.tipo === 'SECAO' ? 'action.hover' : 'background.paper'
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  {campo.tipo === 'SELECAO_UNICA' && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`Pts: ${campo.pontuacaoMax ?? 1}`}
                      sx={{ fontWeight: 700, minWidth: 64 }}
                    />
                  )}
                  {campo.tipo === 'SECAO' && <Chip size="small" label="Tópico" />}
                  <Typography variant="caption" color="text.secondary">
                    #{idx + 1}
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Label"
                    value={campo.label}
                    disabled={readOnly}
                    onChange={(e) => updateCampo(idx, { label: e.target.value })}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    size="small"
                    label="Chave"
                    value={campo.chave}
                    disabled={readOnly}
                    onChange={(e) => updateCampo(idx, { chave: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Tipo"
                    value={campo.tipo}
                    disabled={readOnly}
                    onChange={(e) => {
                      const tipo = e.target.value;
                      updateCampo(idx, {
                        tipo,
                        pontuacaoMax: tipo === 'SELECAO_UNICA' ? 1 : null,
                        opcoes:
                          tipo === 'SELECAO_UNICA' || tipo === 'SELECAO_MULTIPLA'
                            ? campo.opcoes?.length
                              ? campo.opcoes
                              : [...OPCOES_CONFORMIDADE]
                            : []
                      });
                    }}
                    sx={{ minWidth: 160 }}
                  >
                    {TIPOS_CAMPO.map((t) => (
                      <MenuItem key={t.tipo} value={t.tipo}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                  {campo.tipo !== 'SECAO' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!campo.obrigatorio}
                          disabled={readOnly}
                          onChange={(e) => updateCampo(idx, { obrigatorio: e.target.checked })}
                        />
                      }
                      label="Obrigatório"
                    />
                  )}
                  <TextField
                    size="small"
                    label="Seção"
                    value={campo.secao || ''}
                    disabled={readOnly}
                    onChange={(e) => updateCampo(idx, { secao: e.target.value })}
                    sx={{ width: 160 }}
                  />
                  {(campo.tipo === 'SELECAO_UNICA' || campo.tipo === 'SELECAO_MULTIPLA') && (
                    <TextField
                      size="small"
                      label="Opções (vírgula)"
                      value={(campo.opcoes || []).join(', ')}
                      disabled={readOnly}
                      onChange={(e) =>
                        updateCampo(idx, {
                          opcoes: e.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean)
                        })
                      }
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                  )}
                  <Box sx={{ ml: 'auto' }}>
                    <IconButton size="small" disabled={readOnly || idx === 0} onClick={() => move(idx, -1)}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={readOnly || idx === campos.length - 1}
                      onClick={() => move(idx, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={readOnly}
                      onClick={() => setCampos((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {readOnly ? (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            disabled={forking || loading}
            onClick={criarVersaoEditavel}
          >
            {forking ? 'Criando versão…' : 'Editar (nova versão)'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSave} disabled={saving || loading || !(activeId || templateId)}>
            {saving ? 'Salvando…' : 'Salvar campos'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
